import { Injectable, Inject, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LanguageService } from './i18n/language';

const API_URL = '/cargo/open-api/analytics';
const VISITOR_KEY = 'bl_vid';
const SESSION_KEY = 'bl_sid';
const ACTIVITY_KEY = 'bl_sat';
const UTM_KEY = 'bl_utm';
const REFERRER_SENT_KEY = 'bl_ref_sent';
const TOKEN_KEY = 'auth_token';
/** Признак режима «Войти как» (ставит AuthService). */
const IMPERSONATION_KEY = 'impersonation_info';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

interface Utm {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Собственная статистика посетителей без cookie и сторонних сервисов.
 * Шлёт на бэкенд просмотр каждой страницы (в том числе переходы внутри SPA) и время на странице.
 * visitorId живёт в localStorage, sessionId — в sessionStorage и обновляется после 30 минут бездействия.
 * Любые ошибки глотаются: статистика не должна ломать сайт. Приём событий включается на бэкенде
 * (ANALYTICS_ENABLED), пока он выключен, события просто отбрасываются.
 */
@Injectable({ providedIn: 'root' })
export class VisitTrackingService {
  private started = false;
  /** Первую страницу Google-тег учитывает сам (gtag config в index.html) — со второй сообщаем ему о переходах. */
  private gtagFirstView = true;
  private currentPath: string | null = null;
  /** Накопленное время на текущей странице, пока вкладка видима (мс). */
  private visibleMs = 0;
  private visibleSince: number | null = null;

  constructor(
    private router: Router,
    private languageService: LanguageService,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.started = true;

    this.captureUtm();
    this.visibleSince = document.visibilityState === 'visible' ? Date.now() : null;

    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      const path = this.cleanPath(e.urlAfterRedirects);
      if (path === this.currentPath) {
        return;
      }
      this.flushLeave();
      this.currentPath = path;
      this.visibleMs = 0;
      this.visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
      this.sendPageview(path);
      this.sendGtagPageview(path);
    });

    // Выходим из зоны Angular: слушатели не должны запускать проверку изменений
    this.zone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.pauseVisible();
          this.flushLeave();
        } else {
          this.visibleSince = Date.now();
        }
      });
      window.addEventListener('pagehide', () => {
        this.pauseVisible();
        this.flushLeave();
      });
    });
  }

  // ── События ────────────────────────────────────────────────────────────────

  private sendPageview(path: string): void {
    try {
      const sessionId = this.sessionId();
      // Реферер передаём только в первом событии визита: дальше document.referrer остаётся прежним
      // и ошибочно выглядел бы как новый переход.
      let referrer: string | undefined;
      if (sessionStorage.getItem(REFERRER_SENT_KEY) !== sessionId) {
        referrer = document.referrer || undefined;
        sessionStorage.setItem(REFERRER_SENT_KEY, sessionId);
      }
      const body = {
        visitorId: this.visitorId(),
        sessionId,
        path,
        referrer,
        ...this.readUtm(),
        language: this.languageService.getCurrentLanguageValue(),
        screenWidth: window.screen?.width || undefined
      };
      // В режиме «Войти как» работает администратор: его действия не должны попадать в статистику клиента
      if (localStorage.getItem(IMPERSONATION_KEY)) {
        return;
      }
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // Вошедшего пользователя бэкенд определяет по токену, а не по телу запроса
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      fetch(`${API_URL}/pageview`, { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => {});
    } catch {
      // статистика не должна ломать сайт
    }
  }

  /** Переход внутри SPA для Google Ads: без него тег видит только первую открытую страницу. */
  private sendGtagPageview(path: string): void {
    if (this.gtagFirstView) {
      this.gtagFirstView = false;
      return;
    }
    // Заголовок вкладки Angular меняет уже после NavigationEnd — ждём тик, чтобы передать новый
    setTimeout(() => {
      try {
        const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
        gtag?.('event', 'page_view', { page_path: path, page_location: location.href, page_title: document.title });
      } catch {
        // реклама не должна ломать сайт
      }
    });
  }

  private pauseVisible(): void {
    if (this.visibleSince !== null) {
      this.visibleMs += Date.now() - this.visibleSince;
      this.visibleSince = null;
    }
  }

  /** Время на странице (секунды, только пока вкладка видима) → бэкенд, через sendBeacon. */
  private flushLeave(): void {
    try {
      if (!this.currentPath) {
        return;
      }
      const elapsed = this.visibleMs + (this.visibleSince !== null ? Date.now() - this.visibleSince : 0);
      const seconds = Math.round(elapsed / 1000);
      if (seconds <= 0) {
        return;
      }
      const payload = JSON.stringify({ sessionId: this.sessionId(false), path: this.currentPath, seconds });
      // text/plain: sendBeacon не требует preflight; бэкенд принимает JSON и с таким Content-Type
      navigator.sendBeacon(`${API_URL}/leave`, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
    } catch {
      // игнорируем
    }
  }

  // ── Идентификаторы ─────────────────────────────────────────────────────────

  private visitorId(): string {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = this.uuid();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  }

  /** Новый визит — после 30 минут без активности. touch=true обновляет отметку активности. */
  private sessionId(touch = true): string {
    const now = Date.now();
    let id = sessionStorage.getItem(SESSION_KEY);
    const last = Number(sessionStorage.getItem(ACTIVITY_KEY) || 0);
    if (!id || now - last > SESSION_TIMEOUT_MS) {
      id = this.uuid();
      sessionStorage.setItem(SESSION_KEY, id);
      sessionStorage.removeItem(UTM_KEY);
      this.captureUtm();
    }
    if (touch) {
      sessionStorage.setItem(ACTIVITY_KEY, String(now));
    }
    return id;
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Запасной вариант для старых браузеров
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // ── UTM и путь ─────────────────────────────────────────────────────────────

  /** UTM-метки из ссылки запоминаем на весь визит: сайт передаёт их во всех событиях визита. */
  private captureUtm(): void {
    try {
      const q = new URLSearchParams(window.location.search);
      const utm: Utm = {};
      const source = q.get('utm_source');
      const medium = q.get('utm_medium');
      const campaign = q.get('utm_campaign');
      if (source) utm.utmSource = source.slice(0, 100);
      if (medium) utm.utmMedium = medium.slice(0, 100);
      if (campaign) utm.utmCampaign = campaign.slice(0, 100);
      if (Object.keys(utm).length > 0) {
        sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
      }
    } catch {
      // игнорируем
    }
  }

  private readUtm(): Utm {
    try {
      return JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}') as Utm;
    } catch {
      return {};
    }
  }

  /** Путь без домена и query: в query бывают токены приглашений. */
  private cleanPath(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path || '/';
  }
}
